import { useEffect, useMemo, useRef, useState } from 'react';
import { useProductStore } from 'state/store/pdpStore';
import { Button, Spinner, Typography } from '@ag1/ui';
import { CheckoutOptions as CheckoutOptionsProps, SanityKeyed } from 'types/studio/studio';
import { useStoreCurrency } from 'state/hooks/useStoreCurrency';
import { useStoreValue } from 'state/hooks/useStore';
import {
  getCurrentPriceMetafield,
  getOriginalPriceMetafield
} from '@portal/shared/utils/shop/bundle/metafields';

import { getSelectedVariant } from '../utils';
import { usePdpCheckout } from '../usePdpCheckout';
import { useBundlePdpCheckout } from '../useBundlePdpCheckout';
import { StickyCTA } from './StickyCTA';
import styles from './CheckoutOptions.module.scss';
import LegalText from './LegalText/LegalText';
import { CommonSectionProps } from '../../types';

const CheckoutOptions = ({
  _key: key,
  showStickyCTA,
  showStandardCheckout,
  checkoutButtonLabel,
  showSavingLabel,
  savingsLabel,
  savingsLabelPrefix,
  legalText,
  legalTextSuperScript,
  showOldPrice
}: SanityKeyed<CheckoutOptionsProps> & CommonSectionProps) => {
  const { symbolCurrency } = useStoreCurrency();
  const store = useStoreValue();

  const { handleCreatePdpCheckout } = usePdpCheckout();
  const { handleCreateBundlePdpCheckout } = useBundlePdpCheckout();

  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  const buttonRef = useRef<HTMLDivElement>(null);

  const {
    checkoutLoading: isLoadingCheckout,
    checkoutDisabled: disabled,
    isOtpProduct,
    variants,
    flavor,
    pdpType,
    bundleData,
    bundleVariant
  } = useProductStore((state) => state);

  const isLoading = isLoadingCheckout;

  const selectedVariantId = useProductStore((state) => state.selectedVariantId);

  const selectedVariant = useMemo(() => {
    return getSelectedVariant(variants, selectedVariantId, store, flavor);
  }, [variants, selectedVariantId, flavor, store]);

  const parsePrice = (price: string): number => {
    const numericPart = price.split('/')[0];
    return parseFloat(numericPart);
  };

  const isBundlePdP = pdpType === 'bundleView';

  // Get the appropriate prices based on variant type
  const originalPrice =
    isBundlePdP && bundleData
      ? getOriginalPriceMetafield(bundleData)?.value.slice(1)
      : selectedVariant?.oneTimePrice;
  const discountedPrice =
    isBundlePdP && bundleData
      ? getCurrentPriceMetafield(bundleData)?.value.slice(1)
      : selectedVariant?.price;

  let savingsAmount = '0.00';

  if (selectedVariant) {
    if (originalPrice && discountedPrice) {
      savingsAmount = (parsePrice(originalPrice) - parsePrice(discountedPrice)).toFixed(2);
    }
  }

  const showSavingsCallout =
    showSavingLabel && savingsLabel && savingsLabelPrefix && !isOtpProduct && savingsAmount;

  const proceedToCheckout = () => {
    if (isBundlePdP && bundleData) {
      handleCreateBundlePdpCheckout(bundleData);
    } else {
      handleCreatePdpCheckout();
    }
  };

  useEffect(() => {
    const btn = buttonRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsButtonVisible(entry.isIntersecting);
      },
      { threshold: 0 }
    );
    if (btn) observer.observe(btn);

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      if (btn) observer.unobserve(btn);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const buttonLabel = checkoutButtonLabel;

  return (
    <div id={key}>
      <LegalText
        text={legalText || ''}
        showSuperScript={legalTextSuperScript}
        className={styles.legalText}
      />
      {showStandardCheckout && (
        <Button
          ref={buttonRef}
          onClick={proceedToCheckout}
          className={styles.checkoutBtn}
          disabled={disabled || isLoading}
          size={{ l: 'large', xxs: 'medium' }}
          rounded
          icon={!isLoading && 'arrowRight'}
          iconPosition="right">
          {isLoading ? <Spinner color="brand-white" size={24} withOverlay={false} /> : buttonLabel}
        </Button>
      )}

      {!!showSavingsCallout && savingsAmount !== '0.00' && (
        <div className={styles.savingsInfoWrap}>
          <Typography noPadding size="2" variant="body" className={styles.savingsInfo} weight="700">
            {`${savingsLabel} ${symbolCurrency}${savingsAmount}/${savingsLabelPrefix}`}
          </Typography>
        </div>
      )}

      {showStickyCTA && buttonRef.current && (selectedVariant || bundleData) && (
        <StickyCTA
          isVisible={!isButtonVisible && scrollY > buttonRef.current.offsetTop}
          subscriptionLabel={selectedVariant?.subscriptionLabel || bundleVariant?.subscriptionLabel}
          oneTimeTitle={selectedVariant?.oneTimeTitle}
          price={discountedPrice}
          oneTimePrice={originalPrice}
          quantity={selectedVariant?.quantity || 1}
          isOtpProduct={isOtpProduct}
          showStandardCheckout={showStandardCheckout}
          checkoutButtonDisabled={disabled}
          checkoutButtonLabel={buttonLabel}
          onClick={proceedToCheckout}
          showOldPrice={showOldPrice}
          legalTextSuperScript={legalTextSuperScript}
          legalText={legalText}
        />
      )}
    </div>
  );
};

export default CheckoutOptions;
